/**
 * Dormant Variant Group Exception Config
 *
 * Covers: ex-product-ls-variant-group-dormant
 *
 * Variant parent products where every child variant has had no sales
 * in the last 12 months. Candidates for deactivation before a product
 * list migration.
 */
import React from 'react'
import { formatCurrency, formatDate } from '../../../utils/inspectorFormatters'
import CountChip, { CountChipItem } from '../../../components/inspector/shared/CountChip'
import { buildIssuesColumn } from '../shared/issuesColumn'
import { PLATFORM_CONTEXT_COLUMNS } from '../shared/platformColumns'
import {
  composeRowSubtitle,
  datePart,
  skuPart,
  textPart,
} from '../shared/rowSubtitle'
import { MAIN_CATEGORY_COLUMN } from '../shared/productExceptionColumns'
import { formatMainCategory } from '../../../utils/productCategory'
import { productStatusSortValue, renderProductName, renderProductStatus } from '../shared/productRenderers'
import type { ExceptionConfig, ExceptionColumnDef } from '../types'

const PRODUCT_NAME_FROZEN_WIDTH = 350

const renderChildVariants = (
  value: any,
  row: Record<string, any>,
): React.ReactNode => {
  const count = Number(value) || 0
  const rawChildren = Array.isArray(row.child_variants) ? row.child_variants : []
  const items: CountChipItem[] = rawChildren.map((c: any) => ({
    id: c.ls_id,
    primary: c.name || '-',
    secondary: c.sku || null,
    metrics: [c.last_sold ? formatDate(c.last_sold) : '-'],
    link: c.lightspeed_variant_link,
  }))
  return React.createElement(CountChip, {
    count,
    items,
    label: 'Variant Products',
    metricHeaders: ['Last Sold'],
  })
}

const columns: ExceptionColumnDef[] = [
  ...PLATFORM_CONTEXT_COLUMNS,
  buildIssuesColumn(),
  {
    key: 'name',
    label: 'Product Name',
    align: 'left',
    width: PRODUCT_NAME_FROZEN_WIDTH,
    minWidth: PRODUCT_NAME_FROZEN_WIDTH,
    freeze: 'left',
    render: renderProductName,
    link: { field: 'lightspeed_product_link', system: 'lightspeed' },
  },
  {
    key: 'active',
    label: 'Product Status',
    align: 'center',
    render: renderProductStatus,
    getSortValue: productStatusSortValue,
  },
  {
    key: 'sku',
    label: 'SKU',
    render: (value) => value || 'N/A',
  },
  MAIN_CATEGORY_COLUMN,
  {
    key: 'child_variant_count',
    label: 'Variant Products',
    type: 'integer',
    render: renderChildVariants,
    getSortValue: (row) => Number(row.child_variant_count) || 0,
    exportFormat: 'text',
  },
  {
    key: 'last_sold',
    label: 'Last Sold',
    render: (value, _row, ctx) => ctx.variant === 'inspector' ? formatDate(value) : (value || '-'),
    exportFormat: 'date',
  },
  {
    key: 'price_including_tax',
    label: 'Price',
    type: 'currency',
    align: 'right',
    render: (value, _row, ctx) => ctx.variant === 'inspector' ? formatCurrency(value) : undefined,
    exportFormat: 'currency',
  },
]

export const variantGroupDormantConfig: ExceptionConfig = {
  codes: ['ex-product-ls-variant-group-dormant'],
  columns,
  defaultSortColumn: 'last_sold',
  defaultSortDirection: 'asc',
  complexity: 'simple',
  rowKeyField: 'ls_id',
  getRowSubtitle: (row) =>
    composeRowSubtitle([
      skuPart(row.sku),
      typeof row.child_variant_count === 'number' && row.child_variant_count > 0
        ? `${row.child_variant_count} variants`
        : null,
      row.last_sold ? `last sold ${datePart(row.last_sold)}` : null,
      textPart(formatMainCategory(row.category_root_name)),
    ]),
}
